/**
 * 甘特图数据 Hook
 *
 * 获取和转换任务数据，支持筛选和分组
 */

import * as React from 'react'

import {
  issueToGanttTask,
  milestoneToGanttMilestone,
  applyFilters,
  groupTasks,
} from '@/components/pm/gantt/utils'

import { useEmployees } from '../hr/use-employees'

import { useIssues } from './use-issues'
import { useMilestones } from './use-milestones'

import type {
  GanttTask,
  GanttMilestone,
  GanttFilters,
  GroupByOption,
  UseGanttDataReturn,
} from '@/components/pm/gantt/types'


interface UseGanttDataOptions {
  /** 项目 ID */
  projectId: string
  /** 筛选条件 */
  filters: GanttFilters
  /** 分组方式 */
  groupBy: GroupByOption
  /** 展开的分组 */
  expandedGroups: Set<string>
}

export function useGanttData({
  projectId,
  filters,
  groupBy,
  expandedGroups,
}: UseGanttDataOptions): UseGanttDataReturn {
  // 获取任务数据
  const {
    data: issuesData,
    isLoading: issuesLoading,
    error: issuesError,
  } = useIssues({ projectId })

  // 获取里程碑数据
  const {
    data: milestonesData,
    isLoading: milestonesLoading,
    error: milestonesError,
  } = useMilestones(projectId)

  // 获取员工数据（用于显示负责人姓名）
  const { data: employeesData } = useEmployees({})

  // 构建员工 ID -> 姓名映射
  const employeeNameMap = React.useMemo(() => {
    const map = new Map<string, string>()
    if (employeesData?.data) {
      employeesData.data.forEach((emp) => {
        map.set(emp.id, emp.name)
      })
    }
    return map
  }, [employeesData])

  // 构建里程碑 ID -> 名称映射
  const milestoneNameMap = React.useMemo(() => {
    const map = new Map<string, string>()
    if (milestonesData) {
      milestonesData.forEach((ms) => {
        map.set(ms.id, ms.name)
      })
    }
    return map
  }, [milestonesData])

  // 转换任务数据
  const allTasks = React.useMemo<GanttTask[]>(() => {
    if (!issuesData) { return [] }

    return issuesData.map((issue) =>
      issueToGanttTask(
        issue,
        issue.assigneeId ? employeeNameMap.get(issue.assigneeId) : null,
        issue.milestoneId ? milestoneNameMap.get(issue.milestoneId) : null
      )
    )
  }, [issuesData, employeeNameMap, milestoneNameMap])

  // 应用筛选
  const filteredTasks = React.useMemo(
    () => applyFilters(allTasks, filters),
    [allTasks, filters]
  )

  // 应用分组
  const groups = React.useMemo(
    () => groupTasks(filteredTasks, groupBy, expandedGroups),
    [filteredTasks, groupBy, expandedGroups]
  )

  // 转换里程碑数据
  const milestones = React.useMemo<GanttMilestone[]>(() => {
    if (!milestonesData) { return [] }

    return milestonesData
      .map(milestoneToGanttMilestone)
      .filter((ms): ms is GanttMilestone => ms !== null)
  }, [milestonesData])

  // 合并加载状态和错误
  const isLoading = issuesLoading || milestonesLoading
  const error = issuesError || milestonesError || null

  return {
    tasks: filteredTasks,
    milestones,
    groups,
    isLoading,
    error,
    visibleCount: filteredTasks.length,
    totalCount: allTasks.length,
  }
}
